/**
 * Utilidades de data/hora para os módulos da Sprint 3 (Eventos, Reservas,
 * Calendário). Trabalham no fuso LOCAL do navegador; a persistência é sempre
 * em ISO (timestamptz) no banco.
 */
const PAD = (n: number) => String(n).padStart(2, "0");

export const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const DIAS = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
/** Abreviado ("Dom", "Seg"...) — usado em chips de data compactos (ex.: Aniversariantes da Semana). */
export const DIAS_ABREV = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

/** Combina "2026-07-16" + "19:30" (hora local) em um ISO timestamptz. */
export function combinarDataHora(data: string, hora: string): string {
  return new Date(`${data}T${hora}`).toISOString();
}

/** Quebra um ISO em { data: "YYYY-MM-DD", hora: "HH:MM" } no fuso local. */
export function separarDataHora(iso: string): { data: string; hora: string } {
  const d = new Date(iso);
  return {
    data: `${d.getFullYear()}-${PAD(d.getMonth() + 1)}-${PAD(d.getDate())}`,
    hora: `${PAD(d.getHours())}:${PAD(d.getMinutes())}`,
  };
}

/** Chave de dia local "YYYY-MM-DD" a partir de um ISO. */
export function chaveDia(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${PAD(d.getMonth() + 1)}-${PAD(d.getDate())}`;
}

/**
 * F4-17 (revisão 21/07/2026): lista de chaves de dia ("YYYY-MM-DD", fuso
 * local) entre início e fim, incluindo as duas pontas — usado para um evento
 * de mais de um dia aparecer em TODOS os dias que ele ocupa no Calendário
 * Central, não só no dia em que começa. Protegido contra intervalo absurdo
 * (cap em 60 dias) para não travar a tela por engano de dados.
 */
export function diasEntre(inicioISO: string, fimISO: string): string[] {
  const inicio = new Date(inicioISO);
  const fim = new Date(fimISO);
  const cursor = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate());
  const ultimo = new Date(fim.getFullYear(), fim.getMonth(), fim.getDate());
  const dias: string[] = [];
  let guard = 0;
  while (cursor <= ultimo && guard < 60) {
    dias.push(`${cursor.getFullYear()}-${PAD(cursor.getMonth() + 1)}-${PAD(cursor.getDate())}`);
    cursor.setDate(cursor.getDate() + 1);
    guard++;
  }
  return dias.length ? dias : [chaveDia(inicioISO)];
}

/** "16 jul". */
export function formatarDataCurta(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()} ${MESES[d.getMonth()]}`;
}

/** "19:30". */
export function formatarHora(iso: string): string {
  const d = new Date(iso);
  return `${PAD(d.getHours())}:${PAD(d.getMinutes())}`;
}

/** "Quinta, 16 jul". */
export function rotuloDiaLongo(iso: string): string {
  const d = new Date(iso);
  return `${DIAS[d.getDay()]}, ${d.getDate()} ${MESES[d.getMonth()]}`;
}

/** ISO do primeiro instante de hoje (local). */
export function inicioDeHoje(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Soma dias a uma data (novo objeto). */
export function somarDias(base: Date, dias: number): Date {
  const d = new Date(base);
  d.setDate(d.getDate() + dias);
  return d;
}

/**
 * Antecedência mínima (em minutos) para criar Evento/Reserva na data atual.
 * Bloqueia datas passadas e exige folga no dia de hoje. Centralizado aqui de
 * propósito — no futuro pode virar uma configuração do administrador (tela de
 * Configurações da Igreja) sem caçar o valor espalhado pelo código.
 */
export const ANTECEDENCIA_MINIMA_MINUTOS = 60;

/**
 * true se `inicioISO` está no futuro respeitando a antecedência mínima:
 * data passada → false; hoje com menos de X min de folga → false; futuro → true.
 */
export function respeitaAntecedenciaMinima(
  inicioISO: string,
  minutos = ANTECEDENCIA_MINIMA_MINUTOS,
): boolean {
  const limite = new Date();
  limite.setMinutes(limite.getMinutes() + minutos);
  return new Date(inicioISO) >= limite;
}

/** Data de hoje no formato do <input type="date"> (YYYY-MM-DD, fuso local). */
export function dataHojeInput(): string {
  return separarDataHora(new Date().toISOString()).data;
}
